import React, { lazy, Suspense, useState } from "react";
import { createRoot, Root } from "react-dom/client";
import ChatTogglestyles from "./chat-container-style.module.css";
import { SystemMessageProvider } from "../SystemMessage/SystemMessageContext";
import { HelpProvider } from "../HelpContext/helpContext";
import { getBotColor, getBotSetup } from "../../api/widget.api";

const serverUrl = import.meta.env.VITE_API_HOST;

declare global {
  interface Window {
    ChatWidget: {
      mount: (selector: string, props?: any) => void;
      unmount: (selector: string) => void;
      mountToggleButton: (buttonSelector: string, chatSelector: string) => void;
      close: (chatSelector?: string, buttonSelector?: string) => void;
    };
  }
}

const ChatWidget = lazy(() => import("./chatWidget"));

function createContainer(selector: string, className: string): HTMLElement {
  const container = document.createElement('div');
  container.className = `${selector.replace('.', '')} ${className}`;
  
  // Add specific styling for button containers to ensure proper positioning
  if (selector.includes('button')) {
    container.style.position = 'fixed';
    container.style.bottom = '25px';
    container.style.right = '25px';
    container.style.zIndex = '99999999999999999';
    container.style.backgroundColor = 'transparent';
  }
  
  document.body.appendChild(container);
  return container;
}

let userSessionID: string | null | undefined;
let botSettings: any;
let widgetType: string | null | undefined;

let roots: Map<string, Root> = new Map();

function getUserSessionID(): void {
  // const hashParams = new URLSearchParams(window.location.hash.split("?")[1]);
  // let userSessionID = hashParams.get("id");
  // if (hashParams) {
  //   userSessionID = hashParams.get("id");
  //   if (userSessionID) return;
  // }

  document.addEventListener("DOMContentLoaded", () => {

    let scriptTag = Array.from(document.querySelectorAll("script"))
    .find(s => s.src && s.src.includes("widget/chat-widget.iife.js"));

    if (!scriptTag) {
      console.warn("Widget script not found");
      return;
    }

    const scriptUrl = new URL(scriptTag.src);
    const wuuid = scriptUrl.searchParams.get("wuuid");

    if (wuuid) {
      userSessionID = wuuid;
    } else {
      const scriptTagData = document.querySelector("script[data-id]") as HTMLElement;
      const url = scriptTagData?.getAttribute('src');

      if (!url) {
        console.warn("Script with src attribute not found");
        return;
      }

      if (scriptTagData) {
        userSessionID = scriptTagData.getAttribute("data-id");
      } else {
        console.warn("Script with data-id attribute not found");
      }
    }
  });
}

getUserSessionID();

function getWidgetType(): void {
  document.addEventListener("DOMContentLoaded", () => {
    const scriptTag = Array.from(document.querySelectorAll("script"))
      .find(s => s.src && s.src.includes("widget/chat-widget.iife.js"));

    if (!scriptTag) {
      return;
    }

    // Check data-type attribute first
    widgetType = scriptTag.getAttribute('type') || scriptTag.getAttribute('data-type');
    
    // If not found, check script URL query params
    if (!widgetType) {
      try {
        const scriptUrl = new URL(scriptTag.src);
        widgetType = scriptUrl.searchParams.get('type');
      } catch (e) {
        // If URL parsing fails, continue
      }
    }
  });
}

getWidgetType();

const isLightColor = (color: string) => {
  const hex = color.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return (r * 0.299 + g * 0.587 + b * 0.114) > 186;
};


const setupBot = async () => {
  try {
  const abortController = new AbortController();
  const botColor = await getBotColor(abortController.signal, userSessionID as string);
  const customBackgroundClass = `
    .customBackground {
      background-color: ${botColor.themeColor || '#000'};
      color: ${botColor.themeColor ? (isLightColor(botColor.themeColor) ? '#000' : '#fff') : '#fff'};
      border: none
    }

    @keyframes bounceIn {
      0% {
        opacity: 0;
        transform: scale(0.3) translate(50px, 50px);
      }
      50% {
        opacity: 1;
        transform: scale(1.05) translate(0, 0);
      }
      70% {
        transform: scale(0.9) translate(0, 0);
      }
      100% {
        opacity: 1;
        transform: scale(1) translate(0, 0);
      }
    }

    .${ChatTogglestyles.chatWidgetContainer}.${ChatTogglestyles.open} {
      animation: bounceIn 0.6s ease-out;
    }
  `;
  const styleElement = document.createElement('style');
  styleElement.textContent = customBackgroundClass;
  document.head.appendChild(styleElement);
  botSettings = await getBotSetup(abortController.signal, sessionStorage.getItem("accessToken") as string);
  } catch (error) {
    console.error("Error setting up bot:", error);
  }
}

window.ChatWidget = {
  mount: (selector: string, props: any = {}) => {
    let container = document.querySelector(selector);

    if (!container) {
      container = createContainer(
        selector,
        ChatTogglestyles.chatWidgetContainer
      );
    }

    if (!roots.has(selector)) {
      roots.set(selector, createRoot(container));
    }

    // Try to get type from script tag synchronously if not already set
    let typeParam = props.type || widgetType;
    if (!typeParam) {
      const scriptTag = Array.from(document.querySelectorAll("script"))
        .find(s => s.src && s.src.includes("widget/chat-widget.iife.js"));
      if (scriptTag) {
        typeParam = scriptTag.getAttribute('type') || scriptTag.getAttribute('data-type');
        if (!typeParam) {
          try {
            const scriptUrl = new URL(scriptTag.src);
            typeParam = scriptUrl.searchParams.get('type');
          } catch (e) {
            // If URL parsing fails, continue
          }
        }
      }
    }

    const root = roots.get(selector) as Root;
    root.render(
      <HelpProvider>
        <SystemMessageProvider>
          <Suspense fallback={<div>Loading...</div>}>
            <ChatWidget {...props} id={userSessionID} botSettings={botSettings} type={typeParam} />
          </Suspense>
        </SystemMessageProvider>
      </HelpProvider>
    );
  },
  unmount: (selector: string) => {
    const root = roots.get(selector);
    if (root) {
      root.unmount();
      roots.delete(selector);
    } else {
      console.error(`Root not found for selector: ${selector}`);
    }
  },

  mountToggleButton: async (buttonSelector: string, chatSelector: string) => {
    let buttonContainer = document.querySelector(buttonSelector) as HTMLElement;
    let chatContainer = document.querySelector(chatSelector) as HTMLElement;

    if (!buttonContainer) {
      buttonContainer = createContainer(buttonSelector, ChatTogglestyles.button);
    }

    if (!chatContainer) {
      chatContainer = createContainer(chatSelector, ChatTogglestyles.chatWidgetContainer);
    }

    const button = document.createElement('button');
    button.innerHTML = `
      <img src="${serverUrl}/widget/kust_white.svg" alt="Toggle Chat" class="${ChatTogglestyles.chatToggleIcon}"/>
    `;
    button.className = ChatTogglestyles.chatToggleButton;
    button.classList.add('customBackground');
    await setupBot();
    // Trigger a one-time bounce animation after setup completes
    try {
      button.classList.add(ChatTogglestyles.bounceOnce);
      button.addEventListener('animationend', () => {
        button.classList.remove(ChatTogglestyles.bounceOnce);
      }, { once: true });
    } catch (e) {
      // no-op
    }
    button.onclick = () => {
      let isOpen = chatContainer.classList.contains(ChatTogglestyles.open);
      if (isOpen) {
        chatContainer.classList.remove(ChatTogglestyles.open);
      } else {
        chatContainer.classList.add(ChatTogglestyles.open);
        if (!roots.has(chatSelector)) {
          window.ChatWidget.mount(chatSelector);
        }
      }

      const icon = button.querySelector('img');
      if (icon) {
        if (!isOpen) {
          button.innerHTML = `<img src="${serverUrl}/widget/chat_icon_close.svg" alt="Toggle Chat" class="${ChatTogglestyles.chatToggleIcon}"/>`;
        } else {
          button.innerHTML = `<img src="${serverUrl}/widget/chat_icon_white.svg" alt="Toggle Chat" class="${ChatTogglestyles.chatToggleIcon}"/>`;
        }
      }
    };

    buttonContainer.appendChild(button);
  },

  close: (chatSelector: string = ".kust-chat-widget-container", buttonSelector: string = ".kust-chat-widget-button") => {
    try {
      const chatContainer = document.querySelector(chatSelector) as HTMLElement | null;
      if (chatContainer && chatContainer.classList.contains(ChatTogglestyles.open)) {
        chatContainer.classList.remove(ChatTogglestyles.open);
      }

      const buttonContainer = document.querySelector(buttonSelector) as HTMLElement | null;
      const button = buttonContainer ? (buttonContainer.querySelector('button') as HTMLButtonElement | null) : null;
      if (button) {
        button.innerHTML = `<img src="${serverUrl}/widget/chat_icon_white.svg" alt="Toggle Chat" class="${ChatTogglestyles.chatToggleIcon}"/>`;
      }
    } catch (e) {
      console.error("Error closing chat widget:", e);
    }
  }
}


window.addEventListener("DOMContentLoaded", () => {
  if (window.ChatWidget) {
    window.ChatWidget.mountToggleButton(".kust-chat-widget-button", ".kust-chat-widget-container");
  } else {
    console.error("ChatWidget is not defined");
  }
});

export { };
